import {v4 as uuid} from "uuid";
import {IvrActionType, SubmenuSettings, UpdateConnectionPrompt, UpdateIvrMenuMedia} from "@/api";
import {UpdateIvrProfileMutationVariables} from "./mutation.generated";
import {
    AudioFileWriter,
    IvrMediaExport,
    SubmenuSettingsExport,
    UpdateIvrButtonExport,
} from "@/components/domain/export/types";
import {SaveMediaStatus, downloadMedia} from "@/tools";
import {IvrProfileDataSourceQuery} from "./queries.generated";

/** Token used to download media from the API. */
export type MediaDownloadToken = string;

/**
 * Interface that represents a part of an IVR.
 * The properties are identical to what comes back from the API.
 * Only the necessary properties are included to determine the submenu tree.
 **/
export interface IIvr {
    profiles: {
        id: number;
        actions: {
            button: number;
            ivrActionType: IvrActionType;
            targetNumberId?: number;
        }[];
    }[];
    number?: {
        id: number;
    };
}

export type Ivr = IvrProfileDataSourceQuery["currentIvr"]["items"][number];
export type IvrProfile = Ivr["profiles"][number];
export type IvrAction = IvrProfile["actions"][number];
export type UpdateButton = UpdateIvrProfileMutationVariables["buttons"][number];
export type UpdateMenuMedia = UpdateIvrProfileMutationVariables["menuMedia"][number];

export type ButtonPath = number[];
export type ButtonPathString = string;

export type SubmenuDict = {
    [profileId: number]: {
        [path: ButtonPathString]: Ivr;
    };
};

export const buttonPathToString = (path: ButtonPath, ...extra: ButtonPath): ButtonPathString =>
    !path ? "" : path.concat(extra).join("_");

export const buttonPathStringToNumberArray = (path: ButtonPathString, ...toAppend: number[]): ButtonPath => {
    if (!path || path === "") return toAppend;
    return path.split("_").map(Number).concat(toAppend);
};

export const appendToButtonPathString = (path: ButtonPathString, ...toAppend: number[]): ButtonPathString => {
    if (!path || path === "") return toAppend.join("_");
    return path + "_" + toAppend.join("_");
};

export const ivrActionToUpdateButton = (action: IvrAction): UpdateButton => ({
    actionType: action.ivrActionType ?? undefined,
    targetNumberId: action.targetNumberId ?? undefined,
    targetE164: action.targetNumber ?? undefined,
    connectionPrompt: {
        id: action.connectionPrompt?.id ?? undefined,
        downloadToken: action.downloadToken ?? undefined,
        unset: false,
        file: undefined,
        description: action.connectionPrompt?.description ?? undefined,
    },
});

export const greetingsToUpdateMenuMedia = (
    greetings: Ivr["profiles"][number]["greetings"] | undefined,
    isMenuMedia: boolean,
): UpdateIvrMenuMedia[] => {
    if (!greetings) return [];
    return greetings
        .filter((g) => g.isGreeting == !isMenuMedia)
        .sort((a, b) => a.orderNumber - b.orderNumber)
        .map((greeting) => ({
            description: greeting.media.description,
            disabled: greeting.disabled,
            downloadToken: greeting.downloadToken,
            id: greeting.media?.id,
        }));
};

export const ivrActionToExportFormat = async (
    action: IvrAction,
    audioFileWriter: AudioFileWriter,
    accessToken: string,
): Promise<UpdateIvrButtonExport> => {
    const mediaExport = await ivrMediaToExportFormat(action, "ivrAction", audioFileWriter, accessToken);

    return {
        actionType: action.ivrActionType,
        targetE164: action.targetNumber,
        targetNumberId: action.targetNumberId,
        connectionPrompt: mediaExport,
    };
};

interface IvrMediaItem {
    downloadToken?: string;
    media?: {
        disabled?: boolean;
        description?: string;
    };
    connectionPrompt?: {
        disabled?: boolean;
        description?: string;
    };
}
export async function ivrMediaToExportFormat(
    ivrMedia: IvrMediaItem,
    mediaType: "greeting" | "menuMedia" | "ivrAction",
    audioFileWriter: AudioFileWriter,
    accessToken: string,
): Promise<IvrMediaExport> {
    if (!ivrMedia) return undefined; // TODO: better error handling
    if (!ivrMedia.downloadToken) return undefined; // TODO: better error handling

    let mediaKey: keyof IvrMediaItem;
    switch (mediaType) {
        case "greeting":
        case "menuMedia":
            mediaKey = "media";
            break;
        case "ivrAction":
            mediaKey = "connectionPrompt";
            break;
    }

    const {filename, blob, status} = await downloadMedia(accessToken, ivrMedia.downloadToken);

    if (status !== SaveMediaStatus.OK) {
        throw new Error(`Failed to download media: ${status}`);
    }

    const mediaId = uuid();
    const fileExtension = filename.split(".").pop();
    const idFilename = `${mediaId}.${fileExtension}`;

    audioFileWriter(idFilename, blob);

    const media: UpdateConnectionPrompt | UpdateIvrMenuMedia = ivrMedia[mediaKey];

    const result: IvrMediaExport = {
        mediaId: mediaId,
        description: media.description,
        disabled: false,
    };

    if ("disabled" in media) {
        result.disabled = media.disabled;
    }

    return result;
}

export const sortButtons = (a: IvrAction, b: IvrAction) => a.button - b.button;

type Submenu = IvrProfileDataSourceQuery["currentIvr"]["items"][number]["profiles"][number]["submenus"][number];

/// returns a copy  of actions sorted by button
function toSorted(actions: IvrAction[]): IvrAction[] {
    return structuredClone(actions).sort(sortButtons);
}

export function convertSubmenus(profile: IvrProfile): SubmenuSettings[] {
    const submenus: SubmenuSettings[] = [];

    type Greeting = IvrProfile["greetings"][number];
    const sortGreetings = (a: Greeting, b: Greeting) => a.orderNumber - b.orderNumber;

    function findSubmenu(id: number) {
        return profile.submenus.find((s) => s.ivr.number.id == id);
    }

    function addSubmenus(profile: Submenu, basePath: number[]) {
        for (const action of profile.actions) {
            if (action.ivrActionType != IvrActionType.Submenu) continue;
            const submenu = findSubmenu(action.targetNumberId);
            const path = basePath.concat(action.button);
            submenus.push({
                path: path,
                buttons: toSorted(submenu.actions).map(ivrActionToUpdateButton),
                menuMedia: greetingsToUpdateMenuMedia(submenu.greetings.sort(sortGreetings), true),
                instantConnect: submenu.instantConnect,
                timeout: submenu.timeout,
            });
            addSubmenus(submenu, path);
        }
    }
    addSubmenus(profile, []);
    return submenus;
}

export async function convertSubmenusExport(
    profile: IvrProfile,
    audioFileWriter: AudioFileWriter,
    accessToken: string,
): Promise<SubmenuSettingsExport[]> {
    const submenus: SubmenuSettingsExport[] = [];

    type Greeting = IvrProfile["greetings"][number];
    const sortGreetings = (a: Greeting, b: Greeting) => a.orderNumber - b.orderNumber;

    function findSubmenu(id: number) {
        return profile.submenus.find((s) => s.ivr.number.id == id);
    }

    async function addSubmenus(profile: Submenu, basePath: number[]) {
        for (const action of profile.actions) {
            if (action.ivrActionType != IvrActionType.Submenu) continue;
            const submenu = findSubmenu(action.targetNumberId);
            const path = basePath.concat(action.button);
            submenus.push({
                path: path,
                buttons: await Promise.all(
                    toSorted(submenu.actions).map((action) =>
                        ivrActionToExportFormat(action, audioFileWriter, accessToken),
                    ),
                ),
                menuMedia: await Promise.all(
                    submenu.greetings
                        .sort(sortGreetings)
                        .map((greeting) => ivrMediaToExportFormat(greeting, "menuMedia", audioFileWriter, accessToken)),
                ),
                instantConnect: submenu.instantConnect,
                timeout: submenu.timeout,
            });
            await addSubmenus(submenu, path);
        }
    }
    await addSubmenus(profile, []);
    return submenus;
}
